/**
 * Gebäude-Tab im PlanetMenu.
 * Lädt Gebäude vom Server (oder simuliert lokal im Dev-Modus),
 * rendert Karten-Grid, handhabt Upgrades/Downgrades.
 */
import type { SupabaseClient } from '../network/SupabaseClient';
import type { PlanetInfo } from '../world/PlayerWorldTypes';
import type { BuildingType } from '../config/BuildingTypes';
import { ALL_BUILDING_TYPES } from '../config/BuildingTypes';
import { BUILDING_DEFS } from '../config/BuildingsConfig';
import { calcEnergyBalance } from '../systems/EnergyBalance';
import { createBuildingCard } from './BuildingCard';
import { updateHubHeader } from './PlanetHubHeader';
import { DevProductionTick } from '../systems/DevProductionTick';
import { LOADING_STYLE, showHubError } from './HubOverlay';

import { isMobile } from './UITheme';

const SECTIONS: { label: string; icon: string; match: (t: BuildingType) => boolean }[] = [
  { label: 'PRODUKTION', icon: '\u26CF', match: t => BUILDING_DEFS[t].category === 'factory' && t !== 'solar_collector' },
  { label: 'LAGER', icon: '\uD83D\uDCE6', match: t => BUILDING_DEFS[t].category === 'storage' },
  { label: 'ENERGIE', icon: '\u26A1', match: t => t === 'solar_collector' },
];

function gridStyle(): string {
  return isMobile()
    ? 'display:grid;grid-template-columns:repeat(3,1fr);gap:6px;'
    : 'display:grid;grid-template-columns:repeat(auto-fill,minmax(clamp(160px,28vw,220px),1fr));gap:clamp(8px,1.5vw,12px);';
}

const SEC_LINE = 'flex:1;height:1px;background:rgba(136,204,255,0.12);';
function secHdrStyle(): string {
  const m = isMobile();
  return `display:flex;align-items:center;gap:8px;margin:${m ? '10px 0 6px' : '16px 0 8px'};` +
    `font-family:monospace;font-size:${m ? '11px' : '12px'};letter-spacing:2px;color:#4a6a7a;`;
}

export class PlanetMenuBuildings {
  private _supabase: SupabaseClient | null;
  private _devMode: boolean;
  private _loading = false;
  private _devLevels = new Map<string, Map<BuildingType, number>>();
  private _timers = new Map<BuildingType, string>();
  private _cachedLevels = new Map<BuildingType, number>();
  private _cleanups: (() => void)[] = [];
  private _headerEl: HTMLDivElement | null = null;
  private _tick = new DevProductionTick();
  private _onCacheUpdate: ((d: { levels: Map<BuildingType, number>; timers: Map<BuildingType, string> }) => void) | null = null;

  constructor(supabase: SupabaseClient | null, devMode: boolean) {
    this._supabase = supabase;
    this._devMode = devMode;
  }

  setHeaderEl(el: HTMLDivElement): void { this._headerEl = el; }

  async show(
    planet: PlanetInfo,
    container: HTMLElement,
    cached?: { levels: Map<BuildingType, number>; timers: Map<BuildingType, string> },
    onCacheUpdate?: (data: { levels: Map<BuildingType, number>; timers: Map<BuildingType, string> }) => void,
  ): Promise<void> {
    if (this._loading) return;
    this._loading = true;
    if (!cached) container.innerHTML = `<div style="${LOADING_STYLE}">Lade Geb\u00e4ude\u2026</div>`;

    try {
      const { levels, timers } = cached ?? (this._devMode
        ? { levels: this._devLoadLevels(planet.id), timers: new Map<BuildingType, string>() }
        : await this._tickAndLoad(planet));
      this._cachedLevels = levels;
      this._timers = timers;
      if (onCacheUpdate) this._onCacheUpdate = onCacheUpdate;
      this._onCacheUpdate?.({ levels, timers });
      this._cleanups.splice(0).forEach(fn => fn());
      container.innerHTML = '';

      const known = this._devMode || planet.ownership === 'own';
      const canUpgrade = this._devMode || planet.ownership === 'own';
      const energy = calcEnergyBalance(levels);

      if (this._headerEl) {
        updateHubHeader(this._headerEl, planet, levels, energy, known);
      }

      for (const section of SECTIONS) {
        const types = ALL_BUILDING_TYPES.filter(section.match);
        if (types.length === 0) continue;

        const hdr = document.createElement('div');
        hdr.style.cssText = secHdrStyle();
        hdr.innerHTML = `<span>${section.icon} ${section.label}</span>` +
          `<span style="${SEC_LINE}"></span>`;
        container.appendChild(hdr);

        const grid = document.createElement('div');
        grid.style.cssText = gridStyle();
        for (const type of types) {
          const def = BUILDING_DEFS[type];
          const level = levels.get(type) ?? 0;
          const timer = timers.get(type) ?? null;
          const card = createBuildingCard(def, level, known, canUpgrade, {
            onUpgrade: (t, steps) => this._devMode
              ? this._devAdjust(t as BuildingType, planet, container, steps ?? 1)
              : this._upgrade(t, planet, container),
            onDowngrade: (t, steps) => this._devAdjust(t as BuildingType, planet, container, -(steps ?? 1)),
            onTimerDone: () => { this._loading = false; this.show(planet, container); },
          }, this._devMode, timer, this._cleanups, energy.modifier);
          grid.appendChild(card);
        }
        container.appendChild(grid);
      }
      this._tick.start(planet, levels, this._headerEl);
    } finally {
      this._loading = false;
    }
  }

  stopTick(): void { this._tick.stop(); this._cleanups.splice(0).forEach(fn => fn()); }

  private _devLoadLevels(planetId: string): Map<BuildingType, number> {
    if (!this._devLevels.has(planetId)) {
      const m = new Map<BuildingType, number>();
      for (const t of ALL_BUILDING_TYPES) m.set(t, 0);
      this._devLevels.set(planetId, m);
    }
    return this._devLevels.get(planetId)!;
  }

  private _devAdjust(
    type: BuildingType, planet: PlanetInfo, container: HTMLElement, delta: number,
  ): void {
    const levels = this._devLoadLevels(planet.id);
    const current = levels.get(type) ?? 0;
    const target = Math.max(0, Math.min(current + delta, BUILDING_DEFS[type].maxLevel));
    if (target === current) return;
    levels.set(type, target);
    this.show(planet, container);
  }

  // ── Produktion: Supabase ──────────────────────────────────

  private async _tickAndLoad(planet: PlanetInfo): Promise<{
    levels: Map<BuildingType, number>;
    timers: Map<BuildingType, string>;
  }> {
    const levels = new Map<BuildingType, number>();
    const timers = new Map<BuildingType, string>();
    if (!this._supabase) return { levels, timers };

    const { data, error } = await this._supabase.functions.invoke('tick-planet', {
      body: { planet_id: planet.id },
    });
    if (error || !data) { showHubError('Gebäude konnten nicht geladen werden'); return { levels, timers }; }

    if (data?.resources) {
      planet.feronium = data.resources.feronium;
      planet.chrysalium = data.resources.chrysalium;
      planet.neurogel = data.resources.neurogel;
    }
    if (data?.buildings) {
      for (const b of data.buildings) {
        levels.set(b.type as BuildingType, b.level);
        if (b.upgrading_until) timers.set(b.type as BuildingType, b.upgrading_until);
      }
    }
    return { levels, timers };
  }

  private async _upgrade(
    type: string, planet: PlanetInfo, container: HTMLElement,
  ): Promise<void> {
    if (!this._supabase) return;

    const { data, error } = await this._supabase.functions.invoke('upgrade-building', {
      body: { planet_id: planet.id, building_type: type },
    });

    if (error || !data?.success) {
      const ctx = error && 'context' in error ? (error as { context: { error?: string } }).context : null;
      showHubError(ctx?.error ?? data?.error ?? 'Upgrade fehlgeschlagen');
      await this.show(planet, container, { levels: this._cachedLevels, timers: this._timers });
      return;
    }

    if (data.resources) {
      planet.feronium = data.resources.feronium;
      planet.chrysalium = data.resources.chrysalium;
      planet.neurogel = data.resources.neurogel;
    }
    if (data.upgrading_until) {
      this._timers.set(type as BuildingType, data.upgrading_until);
    }

    await this.show(planet, container, { levels: this._cachedLevels, timers: this._timers });
    this._onCacheUpdate?.({ levels: this._cachedLevels, timers: this._timers });
  }

}
